/**
 * Agent keys — `/countries/:cc/admin/agent-keys`. Phase 2.5b Part B.
 *
 * AUTHORED (no Figma exists for the admin surface); every part is an existing
 * primitive: `PageHeader`, `Card`, `TextField`, the states, the table row
 * geometry the inquiries table established (h-row, `border-line` dividers).
 *
 * The screen shows the ACTIVE country's keys (`?country=:cc`): the country is
 * in the URL by design decision, and D3 makes agent keys a country-scoped
 * config object — a grp admin reaches another country's keys by switching
 * country, exactly like every other screen.
 *
 * Revoked keys are hidden by default and revealed by a checkbox rather than
 * omitted from the feature: the row is the audit record that a credential
 * existed and when it stopped working (the API never deletes it), and an admin
 * asking "why did the warehouse stop syncing on Tuesday" needs to SEE the
 * revocation, not infer it from absence.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, Button, Card, Checkbox, EmptyState, SkeletonRows, TextField } from '../../components/ui';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { COUNTRIES } from '../../domain/countries';
import { relativeTime } from '../../domain/events';
import { ApiRequestError } from '../../lib/api';
import {
  AGENT_KEY_LABEL_MAX,
  adminInfraKeys,
  createAgentKey,
  fetchAgentKeys,
  revokeAgentKey,
} from './infra-api';
import type { AgentKeyDto, CreatedAgentKeyDto } from './infra-api';
import { ConfirmDialog } from './ConfirmDialog';
import { ShowOnceKeyDialog } from './ShowOnceKeyDialog';
import { NoAdminAccess, errorSentence } from './states';

export function AgentKeysPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can, isGroupAdmin } = useSession();
  const queryClient = useQueryClient();

  const permitted = isGroupAdmin || can(countryCode, 'config.manage');

  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [label, setLabel] = useState('');
  const [created, setCreated] = useState<CreatedAgentKeyDto | null>(null);
  const [revoking, setRevoking] = useState<AgentKeyDto | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const keys = useQuery({
    queryKey: adminInfraKeys.agentKeys(countryCode, includeRevoked),
    queryFn: ({ signal }) => fetchAgentKeys(countryCode, includeRevoked, signal),
    enabled: permitted,
  });

  const invalidate = (): void => {
    // Both filter variants: a key created while "show revoked" is on must appear
    // in the default list too when the checkbox is cleared.
    void queryClient.invalidateQueries({ queryKey: ['admin', 'agent-keys', countryCode] });
  };

  const create = useMutation({
    mutationFn: (newLabel: string) => createAgentKey(countryCode, newLabel),
    onSuccess: (dto) => {
      setLabel('');
      setActionError(null);
      setCreated(dto);
      invalidate();
    },
    onError: (error) => setActionError(errorSentence(error, 'The key could not be created.')),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeAgentKey(id),
    onSuccess: () => {
      setRevoking(null);
      setActionError(null);
      invalidate();
    },
    onError: (error) => {
      setRevoking(null);
      // The 409 message is the useful one: it names WHEN somebody else already
      // revoked it. Refresh the list so the screen agrees with that fact.
      setActionError(errorSentence(error, 'The key could not be revoked.'));
      if (error instanceof ApiRequestError && error.status === 409) invalidate();
    },
  });

  if (!permitted) return <NoAdminAccess />;

  const rows = keys.data?.data ?? [];
  const countryName = COUNTRIES[countryCode].name;

  return (
    <div className="flex flex-col gap-4 pb-6">
      <PageHeader
        title="Agent keys"
        subtitle={`Credentials the ${countryName} Tally agent presents on every sync. Keys are stored hashed — each one is shown exactly once, at creation.`}
      />

      <div className="flex flex-col gap-4 px-6">
        {actionError === null ? null : <Alert tone="danger">{actionError}</Alert>}

        <Card>
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              const trimmed = label.trim();
              if (trimmed !== '' && !create.isPending) create.mutate(trimmed);
            }}
          >
            <div className="w-full max-w-sm">
              <TextField
                label="Label for a new key"
                value={label}
                maxLength={AGENT_KEY_LABEL_MAX}
                placeholder="e.g. Warehouse PC — Kinshasa"
                onChange={(event) => setLabel(event.target.value)}
              />
            </div>
            <Button type="submit" disabled={label.trim() === '' || create.isPending}>
              {create.isPending ? 'Creating…' : 'Create key'}
            </Button>
          </form>
        </Card>

        <Card
          title={`Keys in ${countryName}`}
          action={
            <Checkbox
              label="Show revoked keys"
              checked={includeRevoked}
              onChange={(event) => setIncludeRevoked(event.target.checked)}
            />
          }
        >
          {keys.isPending ? (
            <SkeletonRows rows={3} label="Loading agent keys" />
          ) : keys.isError ? (
            <EmptyState
              title="Agent keys could not be loaded"
              description={errorSentence(keys.error, 'The server did not answer.')}
            />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<KeyRound aria-hidden="true" className="size-5" />}
              title={includeRevoked ? 'No agent keys' : 'No active agent keys'}
              description={`Create a key and paste it into the ${countryName} agent's configuration.`}
            />
          ) : (
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-line text-th uppercase text-fg-muted">
                  <th scope="col" className="py-2 pr-3 font-normal">
                    Label
                  </th>
                  <th scope="col" className="py-2 pr-3 font-normal">
                    Fingerprint
                  </th>
                  <th scope="col" className="py-2 pr-3 font-normal">
                    Last seen
                  </th>
                  <th scope="col" className="py-2 text-right font-normal">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <AgentKeyRow
                    key={row.id}
                    row={row}
                    onRevoke={() => {
                      setActionError(null);
                      setRevoking(row);
                    }}
                  />
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <ShowOnceKeyDialog created={created} onDone={() => setCreated(null)} />

      <ConfirmDialog
        open={revoking !== null}
        title={`Revoke '${revoking?.label ?? ''}'?`}
        body="The agent using this key loses access on its next request. This cannot be undone — create a new key to reconnect."
        confirmLabel="Revoke key"
        busy={revoke.isPending}
        onConfirm={() => {
          if (revoking !== null) revoke.mutate(revoking.id);
        }}
        onCancel={() => setRevoking(null)}
      />
    </div>
  );
}

function AgentKeyRow({
  row,
  onRevoke,
}: {
  row: AgentKeyDto;
  onRevoke: () => void;
}): React.JSX.Element {
  return (
    <tr className="crf-register-row border-b border-line last:border-b-0">
      {/* `text-fg-secondary`, not muted: a revoked key's NAME is still information
        * (the audit question is "which key stopped working"), and muted measures
        * 2.63:1 on the light surface — under AA for normal-size text. */}
      <td className={`py-2.5 pr-3 text-body ${row.revoked ? 'text-fg-secondary line-through' : 'text-fg'}`}>
        {row.label}
      </td>
      <td className="py-2.5 pr-3 font-mono text-mono text-fg-secondary">{row.fingerprint}</td>
      <td className="py-2.5 pr-3 text-body13 text-fg-secondary">
        {row.revoked ? (
          /* Bordered and unfilled (the sidebar country-pill precedent): the
           * filled alternative — secondary text on bg/subtle — measures 4.4:1
           * in light, just under AA. On the surface it measures 4.64/5.85. */
          <span className="inline-flex items-center rounded-pill border border-fg-muted px-2 py-0.5 text-label text-fg-secondary">
            {/* `revoked_at` is set exactly when `revoked` is true; the fallback
              * exists for the type system, not for a real row. */}
            {`Revoked ${row.revoked_at === null ? '' : relativeTime(row.revoked_at)}`.trim()}
          </span>
        ) : row.last_seen_at === null ? (
          // "Never" is a fact worth stating plainly: a key that was created but
          // never used is either not yet installed or mis-pasted.
          'Never'
        ) : (
          <time dateTime={row.last_seen_at}>{relativeTime(row.last_seen_at)}</time>
        )}
      </td>
      <td className="py-2.5 text-right">
        {row.revoked ? null : (
          /* Danger text forks by mode: #ca2525 measures 5.52:1 on the light
           * surface but 2.98:1 on the dark one — under AA. The dark half uses
           * the pale danger tint as TEXT (13.49:1 on #1f1f1f), and hover
           * restores the pinned 4.52:1 tint pair in both modes. */
          <Button
            variant="quiet"
            className="text-danger-fg hover:bg-danger-bg hover:text-danger-fg dark:text-danger-bg dark:hover:text-danger-fg"
            onClick={onRevoke}
          >
            Revoke…
          </Button>
        )}
      </td>
    </tr>
  );
}
